import { TableColumn } from "react-data-table-component";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../components/title/Title";
import DataTableBase from "../../../components/dataTableBase/DataTableBase";
import { useEffect, useState } from "react";
import { Col } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import SelectOption, {
  ISelectOption,
} from "../../../components/selectOption/SelectOption";
import {
  downloadBill,
  downloadBillByInvoiceNumber,
  getBillHistory,
} from "../../../services/bill";
import { ApiResponse, BillDetails } from "../../../interfaces/billHistory";
import MainLoader from "../../../components/loading/MainLoader";
import { ISuccessState } from "../../../interfaces/successState";
import { getAccountNumbersByToken } from "../../../store/actions/fault.action";
import AlertModal from "../../../components/alertModal/alertModal";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";

interface UserAccount {
  nickname: string;
  accNo: string;
}

const BillHistory = () => {
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billHistory, setBillHistory] = useState<BillDetails[]>([]);
  const [selectedStatusValue, setSelectedStatusValue] = useState<
    ISelectOption | [] | undefined
  >(undefined);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showErrorAlert, setErrorShowAlert] = useState(false);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<BillDetails>();

  // Fetch account numbers on component mount
  useEffect(() => {
    accountNumberHandler();
  }, []);

  const accountNumberHandler = async () => {
    setIsLoading(true);
    try {
      const response = await getAccountNumbersByToken();
      setAccounts(response);

      // Map accounts to ISelectOption format
      const accountOptions = response.map((account: UserAccount) => ({
        label:
          account.nickname === ""
            ? account.accNo
            : account.accNo + " - " + account.nickname,
        value: account.accNo,
      }));

      // Set the initial state to "Select Account Number"
      setSelectedStatusValue(undefined); // Initialize to undefined
      setValue("accountNumber", "");
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchHandler = async (value: string) => {
    if (!value) return;

    setIsLoading(true);
    try {
      const response = await getBillHistory(value);

      const sortedBillHistory = response.sort((b, a) => {
        const dateA = new Date(a.lastBillDate).getTime();
        const dateB = new Date(b.lastBillDate).getTime();
        return dateA - dateB;
      });

      setBillHistory(sortedBillHistory);
    } catch (error) {
      setErrorShowAlert(true);
      setBillHistory([]);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvoiceClick = (invoiceNumber?: string) => {
    if (!invoiceNumber) return;

    setIsLoading(true);

    downloadBillByInvoiceNumber(invoiceNumber)
      .then((resp) => {
        if (resp.dataBundle) {
          setShowAlertSuccess(true);

          const linkSource = `data:application/pdf;base64,${resp.dataBundle}`;
          const downloadLink = document.createElement("a");
          const fileName = "e-bill.pdf";

          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          setShowAlert(true);
        }
      })
      .catch((error) => {
        setShowAlert(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const columns: TableColumn<BillDetails>[] = [
    {
      name: "Invoice Number",
      selector: (row) => row?.invoiceNumber || "N/A",
      sortable: true,
      cell: (row) => (
        <span
          style={{
            color: "blue",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => handleInvoiceClick(row?.invoiceNumber)}
        >
          {row?.invoiceNumber || "N/A"}
        </span>
      ),
    },
    {
      name: "Billing Date",
      selector: (row) => row?.lastBillDate || "N/A",
      center: true,
      sortable: true,
    },
    {
      name: "Balance Forward",
      selector: (row) => {
        const value = row?.balanceForward;
        if (value === undefined || value === null) return "N/A";

        const numValue = typeof value === "string" ? parseFloat(value) : value;

        return isNaN(numValue)
          ? "N/A"
          : numValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      },
      sortable: true,
      center: true,
    },
    {
      name: "Payments Received",
      selector: (row) => {
        const value = row?.lastPaymentAmount;
        if (value === undefined || value === null) return "N/A";

        const numValue = typeof value === "string" ? parseFloat(value) : value;

        return isNaN(numValue)
          ? "N/A"
          : numValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      },
      center: true,
      sortable: true,
    },
    {
      name: (
        <div>
          Charges for
          <br />
          the Period
        </div>
      ),
      selector: (row) => {
        const value = row?.billAmount;
        if (value === undefined || value === null) return "N/A";

        const numValue = typeof value === "string" ? parseFloat(value) : value;

        return isNaN(numValue)
          ? "N/A"
          : numValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      },
      center: true,
      sortable: true,
    },
    {
      name: "Total Payable",
      selector: (row) => {
        const value = row?.outstandingBalance;
        if (value === undefined || value === null) return "N/A";

        const numValue = typeof value === "string" ? parseFloat(value) : value;

        return isNaN(numValue)
          ? "N/A"
          : numValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
      },
      center: true,

      sortable: true,
    },
  ];

  return (
    <div className="page-container">
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/billing/billHistory", title: "Bill History" },
        ]}
      />
      <Title>Bill History</Title>

      <p style={{ textAlign: "center", marginTop: 15, color: "gray" }}>
        Recent 05 invoice details are displayed.
      </p>

      <AlertModal
        show={showAlert}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: () => {
              setShowAlert(false);
            },
            variant: "success",
          },
        ]}
      >
        <label>
          Cannot download invoice. Please contact SLT Account Manager.
        </label>
      </AlertModal>

      <AlertModal
        show={showErrorAlert}
        variant="error"
        buttons={[
          {
            text: "Ok",
            onClick: () => {
              setErrorShowAlert(false);
            },
            variant: "danger",
          },
        ]}
      >
        <label>Bill history is not vailable for this Account Number.</label>
      </AlertModal>

      <Col className="mt-3">
        <Controller
          control={control}
          name="accountNumber"
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <SelectOption
              label="Account Number"
              onChange={(val: ISelectOption) => {
                setSelectedStatusValue(val);
                onChange(val.value);
                searchHandler(val.value);
              }}
              required
              placeholder="Select Account Number"
              options={[
                { label: "Select Account Number", value: "" },
                ...accounts.map((account: UserAccount) => ({
                  label: account.nickname
                    ? account?.accNo + " - " + account?.nickname
                    : account?.accNo,
                  value: account.accNo,
                })),
              ]}
              value={selectedStatusValue}
              errors={[
                {
                  isShow: errors.accountNumber?.type === "required",
                  message: "Account number is required",
                },
              ]}
            />
          )}
        />
      </Col>
      {billHistory && billHistory.length > 0 ? (
        <DataTableBase
          className="mt-3"
          columns={columns}
          data={billHistory}
          progressPending={isLoading}
        />
      ) : (
        <EmptyPlaceholder className="mt-5" />
      )}
    </div>
  );
};

export default BillHistory;
